import { DSVRowArray } from "d3";
import {
  BehaviorSubject,
  combineLatestWith,
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  Subscription,
  switchMap,
  tap
} from "rxjs";
import { Injectable, Optional } from "@angular/core";
import { SliceFilterRestoreService } from "src/app/library/services/core/slice-filter-restore.service";
import { DashboardDataTransformer } from "../data_transformers/dashboard_data_transformer";
import { DatasetsService } from "../../../shared/services/datasets.service";
import { DashboardFilter } from "../filters/dashboard_filter";
import { ProductsState } from "../../../state/products/products.state";
import { FilterSetting } from "../../../shared/graphql/generated/graphql";
import { Store } from "@ngxs/store";
import { DashboardFilterController } from "../filters/dashboard_filter_controller";
import { LocalDatasetsService } from "src/app/shared/services/local-datasets.service";
import {
  DashboardExportController,
  ExportTableMetadata,
} from "../export/export-controller/dashboard-export-controller";
import { RemoteFilterApplicator } from "../filters/dashboard_filter_applicators";
import { Router } from "@angular/router";
import { LocalDatasetsState } from "src/app/state/local_datasets/local_datasets.state";
import { ExportTableStructure } from "../export/table-structure/export-table-structure";
import { Auth } from "src/app/state/auth/auth.action";
import { kpiFilterHeaderWords } from "../dashboard-config/kpis/header-words";


export interface FilterValuesWithData {
  filterValues: Map<string, any>;
  filtersEnabled: Map<string, boolean>;
  data: DSVRowArray | null;
}

export enum DashboardDataStatus {
  AVAILABLE = 'AVAILABLE',
  EMPTY_NO_DATA = 'EMPTY_NO_DATA',
  EMPTY_NO_SELECTION = 'EMPTY_NO_SELECTION',
  EMPTY_NO_FILTER_PASSED = 'EMPTY_NO_FILTER_PASSED',
}


@Injectable()
export class DashboardDataController {
  private _data: DSVRowArray | null = null;
  private _valueSubject: BehaviorSubject<DSVRowArray | null> = new BehaviorSubject<DSVRowArray | null>(null);
  private _filterValuesWithChartDataSubject: BehaviorSubject<FilterValuesWithData | null> = new BehaviorSubject<FilterValuesWithData | null>(null);
  private _dataStatusSubject: BehaviorSubject<DashboardDataStatus | null> = new BehaviorSubject<DashboardDataStatus | null>(null);
  private _exportValueSubject: BehaviorSubject<DSVRowArray | null> = new BehaviorSubject<DSVRowArray | null>(null);
  private _previousRemoteRefreshFilterValues?: Map<string, any>;
  private _isLoadingSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private _errorSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(null); // null
  private _preFilterTransformers: DashboardDataTransformer[] = [];
  private _postFilterTransformers: DashboardDataTransformer[] = [];
  private _chartTransformers: DashboardDataTransformer[] = [];
  private _exportTransformers: DashboardDataTransformer[] = [];

  private _filterController?: DashboardFilterController;

  private _exportController?: DashboardExportController;

  private _filterValueControllerSubscription?: Subscription;


  private _generateMapHeading?: (filterValues: Map<string, any>, selectedProduct: any) => string;
  private _generateChartHeading?: (filterValues: Map<string, any>, selectedProduct: any) => string;
  selectedProduct: any;

  constructor(
    private _datasetsService: DatasetsService,
    private store: Store,
    private _localDatasetsService: LocalDatasetsService,
    private router: Router,
    @Optional() private sliceFilterRestore?: SliceFilterRestoreService,
  ) {
  }

  private _data$: Observable<DSVRowArray | null> = this._valueSubject.asObservable();

  private _filterValuesWithChartData$: Observable<FilterValuesWithData | null> = this._filterValuesWithChartDataSubject.asObservable();

  private _dataStatus$: Observable<DashboardDataStatus | null> = this._dataStatusSubject.asObservable();

  private _exportData$: Observable<DSVRowArray | null> = this._exportValueSubject.asObservable();

  get filterValuesWithChartData$(): Observable<FilterValuesWithData | null> {
    return this._filterValuesWithChartData$
  }

  get data$(): Observable<DSVRowArray | null> {
    return this._data$;
  }

  get dataStatus$(): Observable<DashboardDataStatus | null> {
    return this._dataStatus$;
  }

  get exportData$(): Observable<DSVRowArray | null> {
    return this._exportData$;
  }

  get exportController(): DashboardExportController {
    if (!this._exportController) {
      throw new Error('Controller must be initialized before accessing export controller');
    }
    return this._exportController!;
  }

  private _isLoading$: Observable<boolean> = this._isLoadingSubject.asObservable();

  get isLoading$(): Observable<boolean> {
    return this._isLoading$;
  }

  private _error$: Observable<string | null> = this._errorSubject.asObservable();

  get error$(): Observable<string | null> {
    return this._error$;
  }

  private _chartHeading$!: Observable<string | undefined>;

  get chartHeading$(): Observable<string | undefined> {
    return this._chartHeading$;
  }

  private _mapHeading$!: Observable<string | undefined>;

  get mapHeading$(): Observable<string | undefined> {
    return this._mapHeading$;
  }

  get filterController(): DashboardFilterController {
    if (!this._filterController) {
      throw new Error('Filters must be set before accessing filterController');
    }
    return this._filterController!;
  }

  private _datasetName?: string;
  private _shellId?: string;

  get datasetName(): string {
    if (!this._datasetName) {
      throw new Error('Dataset name must be set before accessing datasetName');
    }
    return this._datasetName!;
  }

  /** Shell package id for library capture / live refresh (from the product definition). */
  get shellId(): string | undefined {
    return this._shellId;
  }

  get preFilterTransformers() {
    return this._preFilterTransformers
  }

  get postFilterTransformers() {
    return this._postFilterTransformers
  }

  get chartTransformers() {
    return this._chartTransformers
  }

  get exportTransformers() {
    return this._exportTransformers
  }

  setDatasetName(datasetName: string) {
    this._datasetName = datasetName;
  }

  setShellId(shellId: string | undefined) {
    this._shellId = shellId;
  }

  setFilterController(filterController: DashboardFilterController) {
    this._filterController = filterController;
  }

  setFilters(filters: DashboardFilter<any>[]) {
    this._filterController = new DashboardFilterController(filters);
  }

  setFilterEnabled(filterName: string, isEnabled: boolean) {
    this.filterController.setFilterEnabled(filterName, isEnabled);
  }

  setFiltersEnabled(filterNames: string[], isEnabled: boolean) {
    filterNames.forEach(filterName => this.setFilterEnabled(filterName, isEnabled));
  }

  setFiltersAndReinitialize(filters: DashboardFilter<any>[]) {
    this.setFilters(filters);
    this.initializeFilterControllerSubscription();
  }

  setPreFilterTransformers(transformers: DashboardDataTransformer[]) {
    this._preFilterTransformers = transformers;
  }

  setPostFilterTransformers(transformers: DashboardDataTransformer[]) {
    this._postFilterTransformers = transformers;
  }

  setChartTransformers(transformers: DashboardDataTransformer[]) {
    this._chartTransformers = transformers;
  }

  setExportTransformers(transformers: DashboardDataTransformer[]) {
    this._exportTransformers = transformers;
  }

  setGenerateChartHeadingCallback(generateChartHeading: (filterValues: Map<string, any>, selectedProduct: any) => string) {
    this._generateChartHeading = generateChartHeading;
  }

  setGenerateMapHeadingCallback(generateMapHeading: (filterValues: Map<string, any>, selectedProduct: any) => string) {
    this._generateMapHeading = generateMapHeading;
  }

  _combineFilterValuesAndData(filterValues: Map<string, any>, filtersEnabled: Map<string, boolean>, data: DSVRowArray): FilterValuesWithData {
    return {filterValues, filtersEnabled, data};
  }

  setFilterValues(filterValues: Map<string, any>) {
    this.filterController.setFilterValues(filterValues);
  }

  setFilterValuesIgnoreMissing(filterValues: Map<string, any>) {
    this.filterController.setFilterValuesIgnoreMissing(filterValues);
  }

  setSliceFilterValues(filterValues: Map<string, any>) {
    this.filterController.setSliceFilterValues(filterValues);
  }

  /** One-shot chart data resolution for library live-preview refresh (no subscriptions). */
  configureForSliceResolve(config: {
    filters: DashboardFilter<any>[];
    initiallyDisabledFilters: string[];
    preFilterTransformers: DashboardDataTransformer[];
    postFilterTransformers: DashboardDataTransformer[];
    chartTransformers: DashboardDataTransformer[];
  }, datasetName: string): void {
    const filters = config.filters;
    config.initiallyDisabledFilters.forEach((name) => {
      const filter = filters.find((f) => f.name === name);
      if (filter) {
        filter.setIsEnabled(false);
      }
    });
    this.setDatasetName(datasetName);
    this.setFilters(filters);
    this.setPreFilterTransformers(config.preFilterTransformers);
    this.setPostFilterTransformers(config.postFilterTransformers);
    this.setChartTransformers(config.chartTransformers);
  }

  snapshotFilterState(): { filterValues: Map<string, any>; filtersEnabled: Map<string, boolean> } {
    const filterValues = new Map<string, any>();
    const filtersEnabled = new Map<string, boolean>();
    this.filterController.filters.forEach((filter) => {
      filterValues.set(filter.name, filter.value);
      filtersEnabled.set(filter.name, filter.isEnabled);
    });
    return { filterValues, filtersEnabled };
  }

  async resolveChartDataOnce(
    filterValues: Map<string, any>,
    filtersEnabled: Map<string, boolean>,
  ): Promise<DSVRowArray | null> {
    let tmp = await this._getDataForFilters(filterValues, filtersEnabled);
    tmp = this._applyPreFilterTransformers(tmp);
    tmp = this._applyRowFiltersToData(tmp);
    tmp = this._applyFiltersToData(tmp);
    tmp = this._applyPostFilterTransformers(tmp);
    return this._applyChartTransformers(tmp).data;
  }

  get data(): DSVRowArray | null {
    return this._data;
  }

  _filterSettingToFilterValues(filterSetting: FilterSetting): Map<string, any> {
    return new Map<string, any>([
      ['methodology', filterSetting.methodologyId?.toString()],
      ['sub_market', filterSetting.submarketId?.toString()],
      ['state', filterSetting.stateId?.toString()],
      ['district', filterSetting.districtId?.toString()],
    ]);
  }

  _applyTransformersToData({filterValues, filtersEnabled, data, transformers}: FilterValuesWithData & {
    transformers: DashboardDataTransformer[]
  }): FilterValuesWithData {
    let tmpData = data == null ? null : JSON.parse(JSON.stringify(data)) as DSVRowArray;
    for (const transformer of transformers) {
      tmpData = transformer.transform(tmpData, filterValues);
    }
    return {filterValues, filtersEnabled, data: tmpData};
  }

  _applyPreFilterTransformers({filterValues, filtersEnabled, data}: FilterValuesWithData): FilterValuesWithData {
    return this._applyTransformersToData({filterValues, filtersEnabled, data, transformers: this._preFilterTransformers});
  }

  _applyPostFilterTransformers({filterValues, filtersEnabled, data}: FilterValuesWithData): FilterValuesWithData {
    return this._applyTransformersToData({filterValues, filtersEnabled, data, transformers: this._postFilterTransformers});
  }

  _applyChartTransformers({filterValues, filtersEnabled, data}: FilterValuesWithData): FilterValuesWithData {
    return this._applyTransformersToData({filterValues, filtersEnabled, data, transformers: this._chartTransformers});
  }

  _applyExportTransformers({filterValues, filtersEnabled, data}: FilterValuesWithData): FilterValuesWithData {
    return this._applyTransformersToData({filterValues, filtersEnabled, data, transformers: this._exportTransformers});
  }

  _getPreviousRemoteRefreshFilterValue(filterName: string) {
    return this._previousRemoteRefreshFilterValues?.get(filterName);
  }

  _applyRowFiltersToData({filterValues, filtersEnabled, data}: FilterValuesWithData): FilterValuesWithData {
    if (data == null) {
      return {filterValues, filtersEnabled, data};
    }
    return {
      filterValues, filtersEnabled, data: <DSVRowArray<string>>data.filter(row => {
        for (const filter of this.filterController.filters) {
          const filterIsEnabled = filtersEnabled.get(filter.name);
          if (!filterIsEnabled) {
            continue;
          }
          if (!filter.rowPassesFilter(row)) {
            return false;
          }
        }
        return true;
      })
    };
  }

  _applyFiltersToData({filterValues, filtersEnabled, data}: FilterValuesWithData): FilterValuesWithData {
    if (data == null) {
      return {filterValues, filtersEnabled, data};
    }
    let tmpData = data;
    for (const filter of this.filterController.filters.filter(filter => filter.applicator != null && filtersEnabled.get(filter.name))) {
      tmpData = filter.applyFilter(tmpData);
    }
    return {filterValues, filtersEnabled, data: tmpData};
  }

  _isDataRefreshFromRemoteNecessary() {
    return this._filterController?.filters.some(filter => filter.dataRefreshFromRemoteNecessary(this._getPreviousRemoteRefreshFilterValue(filter.name)));
  }

  _loadData(apiFilters: Map<string, any>): Promise<DSVRowArray | null> {
    this._errorSubject.next(null);

    return this._datasetsService.getDataset(this.datasetName, apiFilters).then(
      data => {
        if (!data || data.length === 0) {
          this._dataStatusSubject.next(DashboardDataStatus.EMPTY_NO_DATA);
        }
        console.log(`[${this._datasetName}] data loaded from server:`, data);
        this._data = data;
        return data;
      },
      error => {
        if (error.statusCode === 401 || error.message === "401 Unauthorized" || error === "401 Unauthorized") {
          this.store.dispatch(new Auth.Logout())
          return null;
        }
        this._errorSubject.next(error);
        return null;
      }
    );
  }

  initializeFilterControllerSubscription() {
    if (!this._filterController) {
      throw new Error('Filters must be set before initializing');
    }

    if (!this._datasetName) {
      throw new Error('Dataset name must be set before initializing');
    }

    if (this._filterValueControllerSubscription) {
      this._filterValueControllerSubscription.unsubscribe();
    }

    this._filterValueControllerSubscription = this.filterController.filterValues$.pipe(
        combineLatestWith(this.filterController.filtersEnabled$),
        debounceTime(100),
        distinctUntilChanged(),
        switchMap(([filterValues, filtersEnabled]) => this._getDataForFilters(filterValues, filtersEnabled)),
        map(
            filterValuesAndData => {
              // console.time(`[${this._datasetName}] transform`);
              return filterValuesAndData;
            }
        ),
        map(filterValuesAndData => this._applyPreFilterTransformers(filterValuesAndData)),
        tap(filterValuesAndData => {
          console.debug(`[${this._datasetName}] pre-filter-transform data:`, filterValuesAndData.data);
        }),
        map(filterValuesAndData => this._applyRowFiltersToData(filterValuesAndData)),
        map(filterValuesAndData => this._applyFiltersToData(filterValuesAndData)),
        map(filterValuesAndData => this._applyPostFilterTransformers(filterValuesAndData)),
    ).subscribe((data) => {
      const tmp = data as FilterValuesWithData; // FixMe: investigate why this is necessary

      console.debug(`[${this._datasetName}] post-transform filter-values:`, tmp.filterValues);
      console.debug(`[${this._datasetName}] post-transform filter-enabled:`, tmp.filtersEnabled);
      console.debug(`[${this._datasetName}] post-transform data:`, tmp.data);

      const dataStatus = this._getDataStatus(tmp);
      console.debug(`[${this._datasetName}] post-transform data-status:`, dataStatus);
      this._dataStatusSubject.next(dataStatus);

      const chartData = this._applyChartTransformers(tmp).data;
      console.log(`[${this._datasetName}] post-transform chart-data:`, chartData);
      this._valueSubject.next(chartData);
      this._filterValuesWithChartDataSubject.next({
        filterValues: tmp.filterValues,
        filtersEnabled: tmp.filtersEnabled,
        data: chartData
      });

      const exportData = this._applyExportTransformers(tmp).data;
      console.debug(`[${this._datasetName}] post-transform export-data:`, exportData);
      this._exportValueSubject.next(exportData);
    });
  }
  private _getDataStatus({data, filterValues, filtersEnabled}: FilterValuesWithData): DashboardDataStatus {
    if (data === null || data.length === 0) {
      if (this._dataStatusSubject.value === DashboardDataStatus.EMPTY_NO_DATA) {
        // If the status is already set to EMPTY_NO_DATA, then keep it as is
        return DashboardDataStatus.EMPTY_NO_DATA;
      } else if (this._hasEmptyMultiSelectValues(filterValues, filtersEnabled)) {
        return DashboardDataStatus.EMPTY_NO_SELECTION;
      } else {
        return DashboardDataStatus.EMPTY_NO_FILTER_PASSED;
      }
    }

    return DashboardDataStatus.AVAILABLE;
  }

  private _hasEmptyMultiSelectValues(filterValues: Map<string, any>, filtersEnabled: Map<string, boolean>) {
    // Get active multiselect filters
    const activeMultiSelects = Array.from(filterValues.entries())
      .filter(([key, value]) => filtersEnabled.get(key) && Array.isArray(value)) as [string, any[]][];

    // Check if some multiselect filter values are empty
    const isAnyMultiSelectValueEmpty = activeMultiSelects.some(([key, value]) => value.length === 0);

    return isAnyMultiSelectValueEmpty;
  }

  _filterValuesToHeaderWords(filterValues: Map<string, any>): Map<string, string> {
    // Map filterValues of SingleSelectFilter into its name (with some formatting if needed)
    // so it can be easily used at dashboard's chart heading
    const singleSelectFilterValueNames: { [k:string]: Map<any, string> } = {
      state: this._localDatasetsService.getStatesMap(),
      administrative_district: this._localDatasetsService.getAdministrativeDistrictsMap(),
      district: this._localDatasetsService.getDistrictsMap(),
      regional_association: this._localDatasetsService.getRegionalAssociationsMap(),
      sub_market: this._localDatasetsService.getSubmarketMap(),
      wz_class: this._localDatasetsService.getWzMapForHeading(),
      gender: new Map([
        ['female', 'Frauen'],
        ['male', 'Männer']
      ]),
      // Legacy type, index and indicator filter words, from the catalogues.
      ...kpiFilterHeaderWords(),
    }

    const filterValuesFormatted = new Map<string, string>();

    for (const [key, value] of filterValues) {
      if (key in singleSelectFilterValueNames) {
        if (key === 'sub_market') {
          const sumSubMarketIds = this.store.selectSnapshot(LocalDatasetsState.sumSubMarketIds);
          const subMarketName = singleSelectFilterValueNames[key].get(+value)!;
          const subMarketNamePrepositioned = sumSubMarketIds.includes(+value)
            ? `IN DER ${subMarketName.replace(/[Σ∑]/g, '').trim()}`
            : `IM ${subMarketName}-TEILMARKT`
          filterValuesFormatted.set(key, subMarketNamePrepositioned)
        } else if (key === 'wz_class') {
          const wzClassName = singleSelectFilterValueNames[key].get(value)!;
          filterValuesFormatted.set(key, `IM ${wzClassName}`)
        } else if (key === 'state') {
          filterValuesFormatted.set(key, singleSelectFilterValueNames[key].get(+value)!.replace(/[Σ∑]/g, '').trim())
        } else if (key === 'administrative_district' || key === 'district') {
          filterValuesFormatted.set(key, singleSelectFilterValueNames[key].get(+value)!)
        } else if (key === 'regional_association') {
          filterValuesFormatted.set(key, singleSelectFilterValueNames[key].get(+value)!)
        } else {
          filterValuesFormatted.set(key, singleSelectFilterValueNames[key].get(value)!)
        }
      } else {
        if (key === 'years') {
          filterValuesFormatted.set(key, value.join(' - '))
        }
        else if (key === 'periods') {
          filterValuesFormatted.set(key, `${value[0]} - ${value[value.length-1]}`)
        }
        else {
          // this is for multi-select filter and single-select filters that has no name such as 'year'
          filterValuesFormatted.set(key, value)
        }
      }
    }

    return filterValuesFormatted;
  }

  getProductIdFromUrl() {
    const currentUrl = this.router.routerState.snapshot.url;
    const splitUrl = currentUrl.split('/');
    if (splitUrl.length < 3) {
      console.debug(`[data-controller] current url (${currentUrl}) is not a product url`)
      return
    }
    // extract productId from url
    return +splitUrl[2];
  }

  initialize() {
    if (!this._filterController) {
      throw new Error('Filters must be set before initializing');
    }

    if (!this._datasetName) {
      throw new Error('Dataset name must be set before initializing');
    }

    this.initializeFilterControllerSubscription();

    this._chartHeading$ = this.filterController.filterValues$.pipe(
      map(filterValues => {
        const filterValuesWorded = this._filterValuesToHeaderWords(filterValues);
        return this._generateChartHeading ? this._generateChartHeading(filterValuesWorded, this.selectedProduct) : undefined
      })
    );

    this._mapHeading$ = this.filterController.filterValues$.pipe(
      map(filterValues => {
        const filterValuesWorded = this._filterValuesToHeaderWords(filterValues);
        return this._generateMapHeading ? this._generateMapHeading(filterValuesWorded, this.selectedProduct) : undefined
      })
    );

    this.store.select(ProductsState.selectedProductSetting)
      .subscribe({
        next: (selectedProductSetting) => {
          if (!selectedProductSetting) {
            this._applyPendingSliceFilterRestore();
            return;
          }

          const productIdFromUrl = this.getProductIdFromUrl();
          if (productIdFromUrl == null) {
            this._applyPendingSliceFilterRestore();
            return;
          }

          if (selectedProductSetting?.productId != productIdFromUrl) {
            console.debug(`[data-controller] filter setting productId(${selectedProductSetting?.productId}) does not match the productId from url (${productIdFromUrl})`)
            this._applyPendingSliceFilterRestore();
            return;
          }

          console.debug('[data-controller] product\'s filter setting is found', selectedProductSetting)
          this.setFilterValuesIgnoreMissing(this._filterSettingToFilterValues(selectedProductSetting));
          this._applyPendingSliceFilterRestore();
        }
      })

    this.store.select(ProductsState.selectedProduct).subscribe((product: any) => {
      this.selectedProduct = product;
    })
  }

  initializeExportController(tableStructure: ExportTableStructure | undefined, metaData: ExportTableMetadata) {
    this._exportController = new DashboardExportController({inputData: this._exportData$, tableStructure, metaData: metaData});
  }

  private _applyPendingSliceFilterRestore(): void {
    if (!this.sliceFilterRestore) {
      return;
    }

    const currentUrl = this.router.routerState.snapshot.url;
    const parts = currentUrl.split('/');
    const dashboardId = parts[3];
    if (!dashboardId) {
      return;
    }

    const dashboardVariant = parts[4]?.split('?')[0];
    const filterValues = this.sliceFilterRestore.consume(dashboardId, dashboardVariant);
    if (!filterValues) {
      return;
    }

    this.setSliceFilterValues(new Map(Object.entries(filterValues)));
  }

  private async _getDataForFilters(filterValues: Map<string, any>, filtersEnabled: Map<string, boolean>): Promise<FilterValuesWithData> {
    if (this._data == null || this._isDataRefreshFromRemoteNecessary()) {
      this._isLoadingSubject.next(true);
      const filtersAppliedRemotely = this.filterController.getFiltersAppliedRemotely();
      const enabledFiltersAppliedRemotely = filtersAppliedRemotely.filter(filter => filtersEnabled.get(filter.name));
      const filterValuesAppliedRemotely = enabledFiltersAppliedRemotely.reduce((map, filter) => {
        const applicator = filter.applicator as RemoteFilterApplicator;
        map.set(applicator.remoteFilterKey, filterValues.get(filter.name));
        return map;
      }, new Map<string, any>());
      return this._loadData(filterValuesAppliedRemotely).then(data => {
        this._isLoadingSubject.next(false);
        this._previousRemoteRefreshFilterValues = filterValues;
        return {filterValues, data, filtersEnabled};
      });
    }
    this._previousRemoteRefreshFilterValues = filterValues;
    return {filterValues, data: this._data, filtersEnabled};
  }
}
